// @flow

import _ from 'lodash';
import validator from 'validator';
import { checkHasSpecialCharacters, myXss } from 'src/helpers';

/**
 * Check the value is empty
 *
 * @param  {any} value
 *
 * @result {boolean} true if value is empty, false if not
 */
export const isEmpty = (value: any) =>
  value === undefined || value === null || value.toString().trim() === '';

/**
 * Check min value
 *
 * @param  {number} min   min value
 * @param  {number} value value need to be checked
 *
 * @result {string/undefined} string if value < min, undefined if not
 */
export const minValue =
  (min: number, message: string) => (value: number, allValues: Object, props: Object) =>
    (value || value === 0) && value < min
      ? message ||
        props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.minValue', {
          nt_number: min - 1
        })
      : undefined;

/**
 * Check min not equal value
 *
 * @param  {number} min   min value
 * @param  {number} value value need to be checked
 *
 * @result {string/undefined} string if value <= min, undefined if not
 */
export const minNotEqualValue =
  (min: number) => (value: number, allValues: Object, props: Object) =>
    (value || value === 0) && value <= min
      ? props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.minNotEqualValue', {
          nt_number: min
        })
      : undefined;

/**
 * Check max value
 *
 * @param  {number} max   max value
 * @param  {number} value value need to be checked
 *
 * @result {string/undefined} string if value > max, undefined if not
 */
export const maxValue =
  (max: number, text: string = null) =>
  (value: number, allValues: Object, props: Object) => {
    return (value || value === 0) && value > max
      ? text
        ? props.i18n.translate(text, {
            number: max
          })
        : props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.validateMaxValue', {
            number: max
          })
      : undefined;
  };

/**
 * Check max value
 *
 * @param  {number} max   max value
 * @param  {number} value value need to be checked
 *
 * @result {string/undefined} string if value > max, undefined if not
 */
export const maxValue4 =
  (max: number, text: string = null) =>
  (value: number, allValues: Object, props: Object) => {
    return (value || value === 0) && value > max
      ? text
        ? props.i18n.translate(text, {
            number: max
          })
        : props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.validateMaxValue4', {
            number: max
          })
      : undefined;
  };

/**
 * Check min value
 *
 * @param  {number} min   min value
 * @param  {number} value value need to be checked
 *
 * @result {string/undefined} string if value < min, undefined if not
 */
export const isMinValue =
  (min: number, text: string = null) =>
  (value: number, allValues: Object, props: Object) =>
    (value || value === 0) && value < min
      ? text
        ? props.i18n.translate(text, {
            number: min
          })
        : props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.validateMinValue', {
            number: min
          })
      : undefined;

/**
 * Check min value
 *
 * @param  {number} min   min value
 * @param  {number} value value need to be checked
 *
 * @result {string/undefined} string if value < min, undefined if not
 */
export const isMinValue2 =
  (min: number, text: string = null) =>
  (value: number, allValues: Object, props: Object) =>
    (value || value === 0) && value < min
      ? text
        ? props.i18n.translate(text, {
            number: min
          })
        : props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.validateMinValue2', {
            number: min
          })
      : undefined;

/**
 * Check max value
 *
 * @param  {number} max   max value
 * @param  {number} value value need to be checked
 *
 * @result {string/undefined} string if value >= max, undefined if not
 */
export const maxNotEqualValue =
  (max: number) => (value: number, allValues: Object, props: Object) =>
    (value || value === 0) && value >= max
      ? props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.maxNotEqualValue', {
          nt_number: max
        })
      : undefined;

/**
 * Check required value
 *
 * @param  {any} value value need to be checked
 *
 * @result {string/undefined} string if value is empty, undefined if not
 */
export const isRequired =
  (field: string = '') =>
  (value: any, allValues: Object, props: Object) => {
    return isEmpty(value)
      ? field
        ? field
        : props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isRequired')
      : undefined;
  };

/**
 * Check email format
 *
 * @param  {string} value value need to be checked
 *
 * @result {string/undefined} string if value is an email, undefined if not
 */
export const isEmail = () => (value: any, allValues: Object, props: Object) =>
  value &&
  !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i.test(
    value
  )
    ? props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isEmail')
    : undefined;

/**
 * Check email format
 *
 * @param  {string} value value need to be checked
 *
 * @result {string/undefined} string if value is an email, undefined if not
 */
export const isEmailOpt = () => (value: any, allValues: Object, props: Object) => {
  const isValid =
    value &&
    // eslint-disable-next-line
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i.test(
      value
    ) &&
    // eslint-disable-next-line
    /^[\w-\.]+@([\w-]+\.)+[\w-]{2,}$/i.test(value) &&
    !value.includes('@mailinator.') &&
    !value.includes('@mailtothis.');
  if (isValid) return undefined;
  return props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isEmailOpt');
};

/**
 * Check value is an integer (no decimal part)
 *
 * @param  {any} value value need to be checked
 *
 * @result {string/undefined} error string if value is not an integer, undefined if valid
 */
export const isInteger = () => (value: any, allValues: Object, props: Object) => {
  if (value === undefined || value === null || value === '') return undefined;
  if (!Number.isInteger(Number(value))) {
    return props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isInteger');
  }
  return undefined;
};

/**
 * Format number with comma
 *
 * @param  {number} value number to fomat
 *
 * @result {string} string format
 */
export const numberWithCommas = (value: number) => {
  if (value) {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  return value;
};

/**
 * Normalize number, return old value if user type value <> number, "." and ","
 *
 * @param  {number} value number to check
 *
 * @param  {number} prevValue previous number
 *
 * @result {string} string normalize value
 */
export const normalizeNumberWithComma = (value: any, prevValue: number) => {
  if (value) {
    const data = value.replace(/,+/g, '');
    return /^\d+(\.\d{0,2})?$/.test(data) ? data : prevValue;
  }
  return value;
};

/**
 * Normalize number, return old value if user type value <> number
 *
 * @param  {number} value number to check
 *
 * @param  {number} prevValue previous number
 *
 * @result {string} string normalize value
 */
export const normalizeNumber = (value: any, prevValue: number) => {
  if (value) {
    return /^\d+$/.test(value) ? value : prevValue;
  }
  return value;
};

/**
 * Normalize number, return old value if user type value <> number and > max value
 *
 * @param  {number} value number to check
 *
 * @param  {number} prevValue previous number
 *
 * @result {string} string normalize value
 */

export const normalizeNumberWithLimit = (value: any, prevValue: number, max: number) => {
  if (value) {
    return /^\d+$/.test(value) && parseInt(value) <= max ? value : prevValue;
  }
  return value;
};

export const normalizeNumberRangeSlider = (value: any, prevValue: number, max: number = 100) => {
  if (value) {
    const valueInt = parseInt(value);

    return /^\d+$/.test(value) && valueInt <= max ? valueInt : prevValue;
  }
  return value;
};

/**
 * Check contain special characters
 *
 * @param  {string} value input string
 *
 * @return {string}       error message
 */
export const nonSpecialCharacters =
  (field: string = '') =>
  (value: any, allValues: Object, props: Object) => {
    return checkHasSpecialCharacters(value)
      ? field
        ? field
        : props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.nonSpecialCharacters')
      : undefined;
  };

/**
 * Check XSS
 *
 * @param  {string} value input string
 *
 * @return {string}       error message
 */
export const nonXSS =
  (field: string = '') =>
  (value: any, allValues: Object, props: Object) => {
    const sanitizedValue = myXss.process(value);
    return sanitizedValue !== value
      ? field
        ? field
        : props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.nonXSS')
      : undefined;
  };
/**
 * Check XSS V2
 *
 * @param  {string} value input string
 *
 * @return {string}       error message
 */
export const nonXSSv2 =
  (field: string = '') =>
  (value: any, allValues: Object, props: Object) => {
    if (!value) {
      return undefined;
    }
    const sanitizedValue = myXss.process(value);
    return sanitizedValue !== value
      ? field
        ? field
        : props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.nonXSS')
      : undefined;
  };
/**
 * Check is number
 *
 * @param  {string|number} value
 *
 * @return {string}       error message
 */
export const isNumber = (value: any, allValues: Object, props: Object) => {
  return _.isNaN(parseFloat(value))
    ? props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isNumber')
    : undefined;
};

/**
 * Check is phone
 *
 * @param  {string|number} value
 *
 * @return {string}       error message
 */
export const isPhone = (value: unknown, allValues: Object, props: Object) => {
  if (_.isEmpty(value)) return undefined;

  return _.isNaN(parseInt(value))
    ? props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isPhone')
    : undefined;
};

/**
 * Check has tag
 *
 * @param  {any} value value need to be checked
 *
 * @result {string/undefined} string if value is empty, undefined if not
 */

export const hasTag = (value: any, allValues: Object, props: Object) => {
  return /<.*?>/g.test(value)
    ? props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.hasTag')
    : undefined;
};

/**
 * Check is delimiter
 *
 * @param  {any} value value need to be checked
 *
 * @result {string/undefined} string if value is empty, undefined if not
 */
export const isDelimiter = (value: any, allValues: Object, props: Object) => {
  if (!value) {
    return undefined;
  }
  return !/^[.,]?$/g.test(value)
    ? props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isDelimiter')
    : undefined;
};

/**
 * Check valid url
 *
 * @param  {any} value value need to be checked
 *
 * @result {string/undefined} string if value is empty, undefined if not
 */
export const isValidUrl =
  (field: string = '') =>
  (value: string, allValues: Object, props: Object) => {
    return value && !validator.isURL(value)
      ? field
        ? field
        : props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isValidUrl')
      : undefined;
  };

/**
 * Check CDN url
 *
 * @param  {any} value value need to be checked
 *
 * @result {string/undefined} string if value is empty, undefined if not
 */
export const isCDNUrl =
  (field: string = '') =>
  (value: string, allValues: Object, props: Object) => {
    return value &&
      !(
        /^(https?:\/\/)?cdn\.shopify\.com(\/.*)?$/.test(value) ||
        /^(https?:\/\/)?(cdn\.)?shopifycdn\.net(\/.*)?$/.test(value)
      )
      ? field
        ? field
        : props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isCDNUrl')
      : undefined;
  };

/**
 * Check image url
 *
 * @param  {any} value value need to be checked
 *
 * @result {string/undefined} string if value is empty, undefined if not
 */
export const isImageUrl =
  (field: string = '') =>
  (value: string, allValues: Object, props: Object) => {
    return value && value.match(/\.(jpeg|jpg|gif|png|svg|webp)(\?.+)?$/) === null
      ? field
        ? field
        : props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isImageUrl')
      : undefined;
  };

/**
 * Check max length
 *
 * @param  {any} value value need to be checked
 *
 * @result {string/undefined} string if value is empty, undefined if not
 */
export const isExceedLength =
  (maxLength: number = 128, field: string = '') =>
  (value: string, allValues: Object, props: Object) => {
    return value && value.length > maxLength
      ? field
        ? field
        : props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.invalid_Value_Length', {
            nt_number: maxLength
          })
      : undefined;
  };

/**
 * Check is ratio
 *
 * @param  {string|number} value
 *
 * @return {string}       error message
 */
export const isRatio = (value: any, allValues: Object, props: Object) => {
  return value && !/^\d+(:\d+)?$/.test(value)
    ? props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isRatio')
    : undefined;
};

/**
 * Check is ratio
 *
 * @param  {string|number} value
 *
 * @return {string}       error message
 */
export const isRatioV2 = (value: any, allValues: Object, props: Object) => {
  return value && !/^\d+(:\d+)$/.test(value)
    ? props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isRatio')
    : undefined;
};

export const isRatioV2Bool = (value: any) => {
  return value && /^\d+(:\d+)$/.test(value);
};

export const isLimitFileSize =
  (size: number = 2000000) =>
  (file: File, allValues: Object, props: Object) => {
    if (!file) {
      return undefined;
    }
    return file.size > size
      ? props.i18n.translate('AdminHTML.ThemeSetting.Common.limitFileSizeUpload')
      : undefined;
  };

export const isDomain = () => (value: unknown, allValues: Object, props: Object) => {
  const domainRegex = /^(?!-)(?!.*--)[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}(?<!-)$/;
  return !domainRegex.test(value)
    ? props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isValidDomain')
    : undefined;
};

export const isValidCartDrawerId = () => (value: unknown, allValues: Object, props: Object) => {
  const cartDrawerRegex = /^cartdrawer-\d{6}$/;
  if (!value) {
    return undefined;
  }
  return !cartDrawerRegex.test(value)
    ? props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isValidCartDrawerId')
    : undefined;
};
export const isValidTime = (value: any, allValues: Object, props: Object) => {
  const regex = /^\d{1,2}:([0-5][0-9]?\d)$/;
  return value && !regex.test(value)
    ? props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isValidTime')
    : undefined;
};

export const isOnlyNumeric = (value: any, allValues: Object, props: Object) => {
  return /^\d+$/.test(String(value))
    ? undefined
    : props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isNumeric');
};

export const isValidMinuteTime = (value: any, allValues: Object, props: Object) => {
  const numberValue = Number(value);
  return !isNaN(numberValue) && numberValue > 0 && numberValue <= 1440
    ? undefined
    : props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isValidMinuteTime');
};

export const isEndDateAfterStartDate = (value: any, allValues: Object, props: Object) => {
  const startDate = _.get(allValues, 'timerSettings.startDate');
  if (!value || !startDate) return undefined;
  return new Date(value) <= new Date(startDate)
    ? props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isValidStartEndDate')
    : undefined;
};

export const isEndHourAfterStartHour = (value: any, allValues: Object, props: Object) => {
  const startHour = _.get(allValues, 'timerSettings.startHour');
  if (
    value === null ||
    value === undefined ||
    value === '' ||
    startHour === null ||
    startHour === undefined ||
    startHour === ''
  )
    return undefined;
  return parseInt(value, 10) <= parseInt(startHour, 10)
    ? props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.isValidStartEndHour')
    : undefined;
};

/**
 * Check max length
 *
 * @param  {number} max   max length
 * @param  {number} value value need to be checked
 *
 * @result {string/undefined} string if value > max, undefined if not
 */
export const maxLength =
  (max: number, text: string = null) =>
  (value: number, allValues: Object, props: Object) => {
    return value && value.length > max
      ? text
        ? props.i18n.translate(text, {
            number: max
          })
        : props.i18n.translate('AdminHTML.Helpers.FormValidationHelper.validateMaxLength', {
            number: max
          })
      : undefined;
  };

export const isValidTenantId = (value: string): boolean => {
  const tenantIdRegex = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;
  return tenantIdRegex.test(value.trim());
};
